import * as z from 'zod';

export const addressResultSchema = z.object({
  placeId: z.string(),
  structuredFormatting: z.object({
    mainText: z.string(),
    secondaryText: z.string(),
  }),
});

export const deliveryTipSchema = z.object({
  tipAmount: z.number().optional(),
  tipPercentOfSale: z.number().optional(),
});

export const paperGoodsOptionsSchema = z.object({
  plates: z.boolean().optional(),
  cutlery: z.boolean().optional(),
  napkins: z.boolean().optional(),
  cups: z.boolean().optional(),
});

export const orderAdditionalFeeSchema = z.object({
  feeName: z.string().optional(),
  feeAmount: z.number().optional(),
  isWaived: z.boolean().optional(),
});

export const orderDeliveryAddressSchema = z.object({
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  addressLine3: z.string().optional(),
  city: z.string().optional(),
  county: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  zipExtension: z.string().optional(),
  placeId: z.string().optional(),
  companyName: z.string().optional(),
});

export const validatedDeliveryAddressSchema = z.object({
  streetNumber: z.string().optional(),
  route: z.string().optional(),
  subpremise: z.string().optional(),
  locality: z.string().optional(),
  administrativeAreaLevelOne: z.string().optional(),
  administrativeAreaLevelTwo: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});

export const orderPaymentSchema = z.object({
  paymentType: z.string().optional(),
  cardType: z.string().optional(),
  accountNumber: z.string().optional(),
  expirationYear: z.number().optional(),
  expirationMonth: z.number().optional(),
  securityCode: z.string().optional(),
  zipCode: z.string().optional(),
  approvalCode: z.string().optional(),
  deviceSessionId: z.string().optional(),
  dataEventId: z.string().optional(),
  accountId: z.string().optional(),
  availableBalance: z.number().optional(),
  accountType: z.string().optional(),
  cloudPayTransId: z.string().optional(),
  displayName: z.string().optional(),
});

export const orderLineItemModifierSchema = z.object({
  modifierId: z.string().optional(),
  action: z.string().optional(),
  quantity: z.number().optional(),
  name: z.string().optional(),
  priceAdjustment: z.number().optional(),
  compValue: z.number().optional(),
  itemTag: z.string(),
  calories: z.number().optional(),
  groupTag: z.string().optional(),
  emergencyMessage: z.string().optional(),
  didAddMax: z.boolean().optional(),
});

export const orderLineItemComboItemSchema = z.object({
  itemId: z.string().optional(),
  itemTag: z.string(),
  quantity: z.number().optional(),
  modifiers: z.array(orderLineItemModifierSchema).optional(),
  retailPrice: z.number().optional(),
  name: z.string().optional(),
  specialInstructions: z.string().optional(),
  priceAdjustment: z.number().optional(),
  compValue: z.number().optional(),
  rewardValue: z.number().optional(),
  discountClaimId: z.string().optional(),
  imageUri: z.string().optional(),
  calories: z.number().optional(),
  mobileImageUri: z.string().optional(),
  available: z.boolean().optional(),
  promotionId: z.string().optional(),
  promoFree: z.boolean().optional(),
  shouldRemoveSpecialInstructions: z.boolean().optional(),
  emergencyMessage: z.string().optional(),
  dynamicMenuTag: z.string().optional(),
  promotionalItem: z.boolean().optional(),
});

export const orderLineItemSchema = z.object({
  itemId: z.string().optional(),
  itemTag: z.string(),
  quantity: z.number(),
  modifiers: z.array(orderLineItemModifierSchema).optional(),
  comboItems: z.array(orderLineItemComboItemSchema).optional(),
  retailPrice: z.number().optional(),
  name: z.string().optional(),
  specialInstructions: z.string().optional(),
  priceAdjustment: z.number().optional(),
  compValue: z.number().optional(),
  rewardValue: z.number().optional(),
  discountClaimId: z.string().optional(),
  imageUri: z.string().optional(),
  calories: z.number().optional(),
  mobileImageUri: z.string().optional(),
  available: z.boolean().optional(),
  promotionId: z.string().optional(),
  promoFree: z.boolean().optional(),
  shouldRemoveSpecialInstructions: z.boolean().optional(),
  isEdible: z.boolean().optional(),
  emergencyMessage: z.string().optional(),
  promotionalItem: z.boolean().optional(),
});

export const orderSchema = z.object({
  id: z.string(),
  orderId: z.string().optional(),
  cfaId: z.string().optional(),
  locationNumber: z.string().optional(),
  subLocationNumber: z.string().optional(),
  fulfillmentLocationNumber: z.string().optional(),
  lineItems: z.array(orderLineItemSchema).optional(),
  status: z.string().optional(),
  appStatus: z.string().optional(),
  subTotalAmount: z.number().optional(),
  taxAmount: z.number().optional(),
  additionalFees: z.array(orderAdditionalFeeSchema).optional(),
  payment: orderPaymentSchema.optional(),
  destination: z.string().optional(),
  vehicleId: z.string().optional(),
  specialInstructions: z.string().optional(),
  kpsFulfillmentDetails: z.string().optional(),
  rating: z
    .object({
      rating: z.number().optional(),
      tags: z.array(z.string()).optional(),
      comments: z.string().optional(),
      followup: z.boolean().optional(),
    })
    .optional(),
  loyaltySource: z.string().optional(),
  favoriteOrderIds: z.array(z.string()).optional(),
  processLoyalty: z.boolean().optional(),
  cfaLoyalty: z.string().optional(),
  organizationCode: z.string().optional(),
  submitOrderNumber: z.number().optional(),
  sequenceNumber: z.string().optional(),
  timestamps: z
    .object({
      create: z.string().optional(),
      submit: z.string().optional(),
      checkIn: z.string().optional(),
      cancel: z.string().optional(),
      complete: z.string().optional(),
      ready: z.string().optional(),
      bumpDelayReady: z.string().optional(),
      paymentPending: z.string().optional(),
      manualActionCompletedAt: z.string().optional(),
    })
    .optional(),
  kioskCode: z.string().optional(),
  user: z
    .object({
      id: z.string().optional(),
      cfaId: z.string().optional(),
      firstName: z.string().optional(),
      lastInitial: z.string().optional(),
      phoneNumber: z.string().optional(),
    })
    .optional(),
  givenName: z.string().optional(),
  familyName: z.string(),
  compValue: z.number().optional(),
  errorCode: z.number().optional(),
  isAlohaLoyaltyDown: z.boolean().optional(),
  orderType: z.string().optional(),
  promiseDateTime: z.string().optional(),
  orderName: z.string().optional(),
  paperGoods: z.boolean().optional(),
  guestCount: z.number().optional(),
  deliveryAddress: orderDeliveryAddressSchema.optional(),
  partnerSubTotalAmount: z.number().optional(),
  partnerSubTotalDelta: z.number().optional(),
  partnerTaxAmount: z.number().optional(),
  externalOrderInfo: z
    .object({
      customerPrefix: z.string().optional(),
      orderId: z.string().optional(),
      externalOrderSource: z.string().optional(),
    })
    .optional(),
  surveyEligible: z.boolean().optional(),
  cloudPayTransactionId: z.string().optional(),
  companyName: z.string().optional(),
  deliveryFee: z.number().optional(),
  deliverySubscriptionDiscountDollars: z.number().optional(),
  smallDeliveryOrderFee: z.number().optional(),
  smallDeliveryOrderTiers: z
    .array(
      z.object({
        upperThreshold: z.number().optional(),
        fee: z.number().optional(),
      }),
    )
    .optional(),
  asap: z.boolean().optional(),
  additionalOrderStatusURL: z.string().optional(),
  businessDate: z.string().optional(),
  transactionId: z.string().optional(),
  promiseTimeType: z.string().optional(),
  deliveryLogistics: z.string().optional(),
  deliveryStatus: z.string().optional(),
  taxExemptId: z.string().optional(),
  taxExemptAccountName: z.string().optional(),
  bypassBusinessRulesList: z.array(z.string()).optional(),
  deliveryTip: deliveryTipSchema.optional(),
  driverAssociateId: z.string().optional(),
  autoCheckIn: z.boolean().optional(),
  loyaltyPointsEarned: z.number().optional(),
  sendCustomerNotifications: z.boolean().optional(),
  tippableAmount: z.number().optional(),
  clientId: z.string().optional(),
  thirdPartyInAppDetails: z
    .object({
      checkoutUrl: z.string().optional(),
      trackingUrl: z.string().optional(),
    })
    .optional(),
  groupOrderId: z.string().optional(),
  restaurantPickupTime: z.string().optional(),
  timeZone: z.string().optional(),
  secondaryContact: z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      phoneNumber: z.string().optional(),
    })
    .optional(),
  isMobileThru: z.boolean().optional(),
  totalAmount: z.number().optional(),
  cateringReason: z.string().optional(),
  estimatedWaitTime: z.string().optional(),
  hstRegistrationNumber: z.string().optional(),
  feeWaiveThreshold: z.number().optional(),
  smallDeliveryOrderFeeWaiveThreshold: z.number().optional(),
  deliveryFees: z.array(orderAdditionalFeeSchema).optional(),
  recoveryOrder: z.boolean().optional(),
  utcTime: z.string().optional(),
  paperGoodsOptions: paperGoodsOptionsSchema.optional(),
});

export const cateringOrderSchema = orderSchema.extend({
  tranTotal: z.string().optional(),
  deliveryAddress: z.record(z.string(), z.string()).optional(),
});

/** Request body for `POST /1.0`, `PUT /1.0/:id`, and `PUT /1.0/:id/resubmit`. */
export const orderRequestSchema = orderSchema.partial().extend({
  destination: z.string(),
});

export const availableDatesSchema = z.object({
  dates: z.array(z.string()),
});

export const favoriteOrderDetailSchema = z.object({
  orderId: z.string(),
  lineItems: z.array(orderLineItemSchema),
  orderType: z.string(),
  specialInstructions: z.string(),
});

/** Item in the response from `GET /1.1/users/me/favorites`. */
export const favoriteOrderSchema = z.object({
  favoriteOrderId: z.string(),
  name: z.string(),
  count: z.number(),
  orderDetail: favoriteOrderDetailSchema,
  locationNumber: z.string(),
  destination: z.string(),
  createdAt: z.number(),
  available: z.boolean(),
  unavailableReason: z.string().optional(),
});

/** Request body for `POST /1.1/users/me/favorites`. */
export const favoriteOrderRequestSchema = z.object({
  orderId: z.string(),
  name: z.string(),
});

export const cateringDeliveryLocationSchema = z.object({
  locationNumber: z.string(),
  name: z.string(),
  locationContact: z
    .object({
      daytimePhone: z
        .object({
          areaCode: z.string().optional(),
          phoneNumber: z.string().optional(),
          base: z.string().optional(),
          extensionNumber: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  mobileOrderingSettings: z.object({
    fulfillmentMethods: z.object({
      cateringPickup: z.boolean(),
      cateringDelivery: z.boolean(),
    }),
  }),
});

export type AddressResult = z.infer<typeof addressResultSchema>;
export type Order = z.infer<typeof orderSchema>;
export type OrderLineItem = z.infer<typeof orderLineItemSchema>;
export type OrderLineItemComboItem = z.infer<typeof orderLineItemComboItemSchema>;
export type OrderLineItemModifier = z.infer<typeof orderLineItemModifierSchema>;
export type OrderPayment = z.infer<typeof orderPaymentSchema>;
export type OrderAdditionalFee = z.infer<typeof orderAdditionalFeeSchema>;
export type OrderDeliveryAddress = z.infer<typeof orderDeliveryAddressSchema>;
export type ValidatedDeliveryAddress = z.infer<typeof validatedDeliveryAddressSchema>;
export type PaperGoodsOptions = z.infer<typeof paperGoodsOptionsSchema>;
export type DeliveryTip = z.infer<typeof deliveryTipSchema>;
export type CateringOrder = z.infer<typeof cateringOrderSchema>;
export type AvailableDates = z.infer<typeof availableDatesSchema>;
export type OrderRequest = z.infer<typeof orderRequestSchema>;
export type FavoriteOrder = z.infer<typeof favoriteOrderSchema>;
export type FavoriteOrderDetail = z.infer<typeof favoriteOrderDetailSchema>;
export type FavoriteOrderRequest = z.infer<typeof favoriteOrderRequestSchema>;
export type CateringDeliveryLocation = z.infer<typeof cateringDeliveryLocationSchema>;
